import type { Announcement, AnnouncementList } from '~/entities/announcements'
import type { AnnouncementsPopup } from './index.h'

import { combine, createEvent, createStore, sample } from 'effector'
import { model as i18n } from '~/core/i18n'
import { register } from '~/core/repository'
import {
  formatAnnouncementText,
  model as announcements,
} from '~/entities/announcements'

export const init = createEvent()

export const openAnnouncementsPopup = createEvent<AnnouncementList>()

export const nextAnnouncementPopup = createEvent<void>()
export const closeAnnouncementsPopup = createEvent<void>()
export const handleClickButtonAnnouncementsPopup = createEvent<void>()

export const $announcementsPopup = createStore<AnnouncementsPopup>({
  announcements: [],
  condition: false,
})
  .on(openAnnouncementsPopup, (data, payload) => {
    const list = payload.announcements
      .filter(
        (item) => item.type === 'Announcement' || item.type === 'Reminder'
      )
      .map((announcement) => ({
        ...announcement,
        subject: formatAnnouncementText(
          announcement.subject ?? '',
          payload.tokens
        ),
        message: formatAnnouncementText(announcement.message, payload.tokens),
      }))

    const updatedAnnouncements = data.announcements.concat(list)

    return {
      announcements: updatedAnnouncements,
      condition: updatedAnnouncements.length > 0,
    }
  })
  .reset(closeAnnouncementsPopup)

export const $totalAnnouncements = $announcementsPopup.map(
  ({ announcements }) => announcements.length
)

export const $currentCountAnnouncement = createStore<number>(1)
  .on(nextAnnouncementPopup, (data) => data + 1)
  .reset(closeAnnouncementsPopup)

export const $currentAnnouncement = combine(
  $announcementsPopup,
  $currentCountAnnouncement,
  (total, current) => total.announcements[current - 1] ?? null
)

export const $counter = combine(
  $totalAnnouncements,
  $currentCountAnnouncement,
  (total, current) => (total > 1 ? `${current}/${total}` : '')
)

export const $announcementButtonPopup = combine(i18n.$t, ({ t }) => ({
  okButton: t('ok'),
  nextButton: t('next'),
}))

export const $buttonTextAnnouncementsPopup = combine(
  {
    counter: $counter,
    current: $currentCountAnnouncement,
    total: $totalAnnouncements,
    translations: $announcementButtonPopup,
  },
  ({ counter, current, total, translations }) => {
    if (Boolean(counter) && current < total) return translations.nextButton
    return translations.okButton
  }
)

sample({
  clock: announcements.received,
  filter: ({ announcements }) =>
    Boolean(announcements && announcements.length > 0),
  fn: (payload): AnnouncementList => ({
    announcements: (payload.announcements ?? []).filter(
      (item): item is Announcement =>
        item.buttonType != null && item.message != null
    ),
    tokens: { ...payload.tokens },
  }),
  target: openAnnouncementsPopup,
})

sample({
  clock: handleClickButtonAnnouncementsPopup,
  source: {
    counter: $counter,
  },
  filter: ({ counter }) => Boolean(counter),
  target: nextAnnouncementPopup,
})

sample({
  clock: handleClickButtonAnnouncementsPopup,
  source: {
    counter: $counter,
    current: $currentCountAnnouncement,
    total: $totalAnnouncements,
  },
  filter: ({ counter, current, total }) => !counter || current > total,
  target: closeAnnouncementsPopup,
})

register(openAnnouncementsPopup, 'openAnnouncementsPopup')
register(nextAnnouncementPopup, 'nextAnnouncementPopup')
register(closeAnnouncementsPopup, 'closeAnnouncementsPopup')
register(
  handleClickButtonAnnouncementsPopup,
  'handleClickButtonAnnouncementsPopup'
)
register($announcementsPopup, '$announcementsPopup')
register($totalAnnouncements, '$totalAnnouncements')
register($currentAnnouncement, '$currentAnnouncement')
register($currentCountAnnouncement, '$currentCountAnnouncement')
register($counter, '$counterAnnouncement')
register($buttonTextAnnouncementsPopup, '$buttonTextAnnouncementsPopup')
